import { createSlice } from '@reduxjs/toolkit';
import { calcularPrecioConDescuento } from '../utils/descuentoUtils';
import { getPrimerProductoAgregadoAtDesdeExpiracion } from 'utils/carritoExpiracion';

const initialState = {
	items: [],
	cantidadProductos: 0,
	precioTotal: 0,
	costoEnvio: 0,
	zonaEnvio: 0,
	zonaEnvioNombre: '',
	comisionMP: 0,
	direccionId: 0,
	pedidoId: '',
	deliveryMethod: '',
	sucursalId: 0,
	notas: '',
	notasCocinera: '',
	cupon: '',
	primerProductoAgregadoAt: null,
};

const toNumber = (value, fallback = 0) => {
	const number = Number(value);
	return Number.isFinite(number) ? number : fallback;
};

// El backend guarda estos valores al agregar el ítem. Los fallbacks permiten
// leer carritos creados antes de la migración.
const precioProductoCarrito = (item) =>
	toNumber(
		item.precioUnitarioCarrito ??
			item.precioConDescuento ??
			calcularPrecioConDescuento(item.precio, item.descuentos || []),
	);

const costoAdicionalCarrito = (item) =>
	toNumber(item.costoAdicionalCarrito ?? item.adicional?.costoAdicional);

const precioPackCarrito = (item) =>
	toNumber(
		item.precioConAdicionalesCarrito ??
			item.precioConAdicionales ??
			item.precioUnitarioCarrito ??
			item.precio_final ??
			item.precio,
	);

const carritoSlice = createSlice({
	name: 'carrito',
	initialState,
	reducers: {
		agregarItem: (state, action) => {
			const newItem = action.payload;

			// Handle packs differently from products
			if (newItem.tipo === 'pack') {
				const existingPack = state.items.find((item) => item.id === newItem.id && item.tipo === 'pack');

				const precioUsado = existingPack ? precioPackCarrito(existingPack) : precioPackCarrito(newItem);

				if (existingPack) {
					// Increment quantity for existing pack
					existingPack.cantidad += newItem.cantidad || 1;
					existingPack.precioTotal += precioUsado * (newItem.cantidad || 1);
				} else {
					// Add new pack with all its data including productos array with selected additionals
					state.items.push({
						id: newItem.id,
						nombre: newItem.nombre,
						titulo: newItem.nombre || newItem.titulo,
						descripcion: newItem.descripcion,
						tipo: 'pack',
						precio: newItem.precio,
						precioListaUnitarioCarrito: newItem.precioListaUnitarioCarrito ?? newItem.precio,
						precio_final: newItem.precio_final,
						precio_calculado: newItem.precio_calculado,
						precioConAdicionales: newItem.precioConAdicionales,
						precioUnitarioCarrito: newItem.precioUnitarioCarrito,
						precioConAdicionalesCarrito: newItem.precioConAdicionalesCarrito ?? precioUsado,
						ahorro: newItem.ahorro,
						cantidad: newItem.cantidad || 1,
						precioTotal: precioUsado * (newItem.cantidad || 1),
						productos: newItem.productos || [], // Preserves selected additionals
						imageSrc: newItem.imagen || null,
						imagen: newItem.imagen,
					});
				}
				state.cantidadProductos += newItem.cantidad || 1;
				state.precioTotal += precioUsado * (newItem.cantidad || 1);
			} else if (newItem.tipo === 'vianda') {
				const existingVianda = state.items.find(
					(item) =>
						item.id === newItem.id &&
						item.tipo === 'vianda' &&
						(item.viandasDiarias || 1) === (newItem.viandasDiarias || 1),
				);

				const precioUnitario = toNumber(newItem.precioUnitarioCarrito ?? newItem.precio);
				if (existingVianda) {
					existingVianda.cantidad += newItem.cantidad || 1;
					existingVianda.precioTotal += precioUnitario * (newItem.cantidad || 1);
				} else {
					state.items.push({
						id: newItem.id,
						titulo: newItem.titulo || newItem.nombre,
						descripcion: newItem.descripcion,
						tipo: 'vianda',
						precio: precioUnitario,
						precioUnitarioCarrito: precioUnitario,
						cantidad: newItem.cantidad || 1,
						precioTotal: precioUnitario * (newItem.cantidad || 1),
						viandasDiarias: newItem.viandasDiarias || 1,
						cantidadDias: newItem.cantidadDias || newItem.cantidad_dias || 0,
						descuentoId: newItem.descuentoId || newItem.descuento_id || null,
						imageSrc: newItem.imagen || newItem.imageSrc || null,
					});
				}

				state.cantidadProductos += newItem.cantidad || 1;
				state.precioTotal += precioUnitario * (newItem.cantidad || 1);
			} else {
				// Logic for products with discount calculation
				const descuentos = newItem.descuentos || [];
				const precioConDescuento = toNumber(
					newItem.precioUnitarioCarrito ??
						newItem.precioConDescuento ??
						calcularPrecioConDescuento(newItem.precio, descuentos),
				);
				const adicionalCosto = toNumber(newItem.costoAdicionalCarrito ?? newItem.adicional?.costoAdicional);
				const precioFinalUnitario = precioConDescuento + adicionalCosto;

				const existingItem = state.items.find(
					(item) =>
						item.id === newItem.id &&
						item.adicional?.id === newItem.adicional?.id &&
						item.tipo === newItem.tipo,
				);
				const precioFinalParaItem = existingItem
					? precioProductoCarrito(existingItem) + costoAdicionalCarrito(existingItem)
					: precioFinalUnitario;

				if (existingItem) {
					existingItem.cantidad += newItem.cantidad || 1;
					existingItem.precioTotal += precioFinalParaItem * (newItem.cantidad || 1);
				} else {
					state.items.push({
						id: newItem.id,
						titulo: newItem.titulo,
						descripcion: newItem.descripcion,
						tipo: newItem.tipo,
						categoria: newItem.categoria || null,
						precio: newItem.precio,
						precioConDescuento: precioConDescuento, // Guardar precio con descuento
						precioListaUnitarioCarrito: newItem.precioListaUnitarioCarrito ?? newItem.precio,
						precioUnitarioCarrito: precioConDescuento,
						cantidad: newItem.cantidad || 1,
						precioTotal: precioFinalUnitario * (newItem.cantidad || 1),
						adicional: newItem.adicional
							? { ...newItem.adicional, costoAdicionalCarrito: adicionalCosto }
							: null,
						descuentos: descuentos,
						imageSrc: newItem.imagen ? newItem.imagen : null,
					});
				}
				state.cantidadProductos += newItem.cantidad || 1;
				state.precioTotal += precioFinalParaItem * (newItem.cantidad || 1);
			}

			state.pedidoId = newItem.pedido_id;
			// La vigencia se extiende desde la última modificación efectiva.
			state.primerProductoAgregadoAt = Date.now();
		},

		quitarItem: (state, action) => {
			const { id, tipo, adicional, cantidad = 1, viandasDiarias } = action.payload;

			// Buscar el índice del producto-adicional en el carrito
			const existingItemIndex = state.items.findIndex(
				(item) =>
					item.id === id &&
					item.tipo === tipo &&
					(tipo !== 'vianda' || (item.viandasDiarias || 1) === (viandasDiarias || 1)) &&
					(adicional ?
						item.adicional?.id === adicional.id // Verificar adicional coincidente
					:	!item.adicional), // Buscar productos sin adicional si adicional es null
			);

			if (existingItemIndex !== -1) {
				const existingItem = state.items[existingItemIndex];

				const precioUnitario =
					existingItem.tipo === 'pack' ? precioPackCarrito(existingItem)
					: existingItem.tipo === 'vianda' ? toNumber(existingItem.precioUnitarioCarrito ?? existingItem.precio)
					: precioProductoCarrito(existingItem) + costoAdicionalCarrito(existingItem);

				if (existingItem.cantidad > cantidad) {
					// Reducir cantidad del producto-adicional
					existingItem.cantidad -= cantidad;
					existingItem.precioTotal -= precioUnitario * cantidad;

					// Ajustar los totales
					state.cantidadProductos -= cantidad;
					state.precioTotal -= precioUnitario * cantidad;
				} else {
					// Eliminar el producto-adicional del carrito
					state.cantidadProductos -= existingItem.cantidad;
					state.precioTotal -= existingItem.precioTotal;
					state.items.splice(existingItemIndex, 1);
				}
			}

			// Si el carrito queda vacío, limpiar pedidoId y direccionId
			if (state.cantidadProductos < 1) {
				state.pedidoId = '';
				state.direccionId = 0;
			}
			if (existingItemIndex !== -1) {
				state.primerProductoAgregadoAt = Date.now();
			}
		},

		incrementarCantidad: (state, action) => {
			const { id, tipo, adicional, viandasDiarias } = action.payload;

			const existingItem = state.items.find(
				(item) =>
					item.id === id &&
					item.tipo === tipo &&
					(tipo !== 'vianda' || (item.viandasDiarias || 1) === (viandasDiarias || 1)) &&
					(adicional ? item.adicional?.id === adicional.id : !item.adicional),
			);

			if (existingItem) {
				const precioUnitario =
					existingItem.tipo === 'pack' ? precioPackCarrito(existingItem)
					: existingItem.tipo === 'vianda' ? toNumber(existingItem.precioUnitarioCarrito ?? existingItem.precio)
					: precioProductoCarrito(existingItem) + costoAdicionalCarrito(existingItem);

				existingItem.cantidad += 1;
				existingItem.precioTotal += precioUnitario;
				state.cantidadProductos += 1;
				state.precioTotal += precioUnitario;
				state.primerProductoAgregadoAt = Date.now();
			}
		},

		decrementarCantidad: (state, action) => {
			const { id, tipo, adicional, viandasDiarias } = action.payload;

			const existingItem = state.items.find(
				(item) =>
					item.id === id &&
					item.tipo === tipo &&
					(tipo !== 'vianda' || (item.viandasDiarias || 1) === (viandasDiarias || 1)) &&
					(adicional ? item.adicional?.id === adicional.id : !item.adicional),
			);

			if (existingItem) {
				const precioUnitario =
					existingItem.tipo === 'pack' ? precioPackCarrito(existingItem)
					: existingItem.tipo === 'vianda' ? toNumber(existingItem.precioUnitarioCarrito ?? existingItem.precio)
					: precioProductoCarrito(existingItem) + costoAdicionalCarrito(existingItem);

				if (existingItem.cantidad > 1) {
					existingItem.cantidad -= 1;
					existingItem.precioTotal -= precioUnitario;
					state.cantidadProductos -= 1;
					state.precioTotal -= precioUnitario;
					state.primerProductoAgregadoAt = Date.now();
				} else {
					// Si la cantidad es 1, elimina el producto-adicional
					state.items = state.items.filter(
						(item) =>
							!(
								item.id === id &&
								item.tipo === tipo &&
								(tipo !== 'vianda' || (item.viandasDiarias || 1) === (viandasDiarias || 1)) &&
								(adicional ? item.adicional?.id === adicional.id : !item.adicional)
							),
					);
					state.cantidadProductos -= 1;
					state.precioTotal -= precioUnitario;
					state.primerProductoAgregadoAt = Date.now();
				}
			}

			if (state.cantidadProductos < 1) {
				state.pedidoId = '';
				state.direccionId = 0;
			}
		},

		limpiarCarrito: (state) => {
			state.items = [];
			state.cantidadProductos = 0;
			state.precioTotal = 0;
			state.costoEnvio = 0;
			state.zonaEnvio = 0;
			state.zonaEnvioNombre = '';
			state.pedidoId = '';
			state.direccionId = 0;
			state.sucursalId = 0;
			state.notas = '';
			state.notasCocinera = '';
			state.cupon = '';
			state.primerProductoAgregadoAt = null;
		},

		setCostoEnvio: (state, action) => {
			state.costoEnvio = action.payload;
		},

		setZonaEnvio: (state, action) => {
			state.zonaEnvio = action.payload;
		},

		setZonaEnvioNombre: (state, action) => {
			state.zonaEnvioNombre = action.payload;
		},

		setComisionMP: (state, action) => {
			state.comisionMP = action.payload;
		},

		setCarrito: (state, action) => {
			// Transforma los datos del backend al formato esperado por el estado del carrito
			const { productos = [], packs = [], viandas = [], id, carrito_expira_at: carritoExpiraAt } = action.payload;

			const items = [
				...productos.map((producto) => {
					const adicionalCosto = toNumber(
						producto.adicional?.costo_unitario_carrito ?? producto.adicional?.costoAdicional,
					);

					// Adaptar estructura de descuento del backend a estructura del frontend
					let descuentos = [];
					if (producto.descuento) {
						// Mapear tipo_id a tipo string
						const tipo = producto.descuento.tipo_id === 1 ? 'PORCENTAJE' : 'IMPORTE';

						descuentos = [
							{
								descuento: {
									id: producto.descuento.id,
									tipo: tipo,
									valor: producto.descuento.valor,
									descripcion: producto.descuento.descripcion,
								},
							},
						];
					}

					// Calcular precioConDescuento
					let precioConDescuento;
					if (producto.precio_unitario_carrito != null) {
						precioConDescuento = toNumber(producto.precio_unitario_carrito);
					} else if (producto.precioTotalConDescuentos) {
						// Backend envía el precio unitario con descuento SIN adicional.
						precioConDescuento = toNumber(producto.precioTotalConDescuentos);
					} else if (descuentos.length > 0) {
						// Calcular con la función si tenemos descuentos
						precioConDescuento = calcularPrecioConDescuento(producto.precio, descuentos);
					} else if (producto.precioTotal) {
						// Extraer desde precioTotal (ya incluye adicional, por eso lo restamos)
						precioConDescuento = producto.precioTotal / producto.cantidad - adicionalCosto;
					} else {
						// Fallback: usar precio original
						precioConDescuento = producto.precio;
					}

					return {
						id: producto.id,
						titulo: producto.titulo,
						descripcion: producto.descripcion,
						tipo: 'producto', // Identifica como producto
						categoria: producto.categoria || null,
						precio: producto.precio,
						precioListaUnitarioCarrito: toNumber(producto.precio_lista_unitario_carrito ?? producto.precio),
						precioConDescuento: precioConDescuento, // Guardar precio con descuento
						precioUnitarioCarrito: precioConDescuento,
						cantidad: producto.cantidad,
						precioTotal: producto.precioTotal || (precioConDescuento + adicionalCosto) * producto.cantidad,
						adicional: producto.adicional
							? { ...producto.adicional, costoAdicionalCarrito: adicionalCosto }
							: null,
						descuentos: descuentos, // Descuentos en formato del frontend
						imageSrc: producto.imagen ? atob(producto.imagen) : null,
					};
				}),
				...packs.map((pack) => ({
					id: pack.id,
					nombre: pack.nombre,
					titulo: pack.nombre, // Para compatibilidad con CartItem
					descripcion: pack.descripcion || pack.nombre,
					tipo: 'pack', // Identifica como pack
					precio: pack.precio,
					precio_final: pack.precio_final,
					precio_calculado: pack.precio_calculado,
					precioConAdicionales: pack.precioConAdicionales,
					precioUnitarioCarrito: toNumber(pack.precio_unitario_carrito ?? pack.precio_final ?? pack.precio),
					precioConAdicionalesCarrito: toNumber(
						pack.precioConAdicionales_carrito ?? pack.precioConAdicionales ?? pack.precio_unitario_carrito ?? pack.precio,
					),
					ahorro: pack.ahorro,
					cantidad: pack.cantidad,
					precioTotal:
						toNumber(pack.precioConAdicionales_carrito ?? pack.precioConAdicionales ?? pack.precio_unitario_carrito ?? pack.precio) *
						pack.cantidad,
					productos: pack.productos || [], // Información de productos con adicionales
					imageSrc: pack.imagen ? pack.imagen : null,
					imagen: pack.imagen,
				})),
				...viandas.map((vianda) => ({
					id: vianda.id,
					titulo: vianda.nombre || vianda.titulo || `Plan ${vianda.cantidad_dias || ''} días`,
					descripcion: vianda.descripcion || '',
					tipo: 'vianda',
					precio: vianda.precio_unitario || vianda.precio || (vianda.precioTotal || 0) / (vianda.cantidad || 1),
					cantidad: vianda.cantidad || 1,
					precioTotal:
						vianda.precioTotal ||
						(vianda.precio_unitario || vianda.precio || 0) * (vianda.cantidad || 1),
					viandasDiarias: vianda.viandasDiarias || vianda.viandas_diarias || 1,
					cantidadDias: vianda.cantidadDias || vianda.cantidad_dias || 0,
					descuentoId: vianda.descuento_id || vianda.descuento?.id || null,
					imageSrc: vianda.imagen || null,
				})),
			];

			state.items = items;
			state.cantidadProductos = items.reduce((total, item) => total + item.cantidad, 0);
			// Calcular precioTotal sumando los precioTotal de cada item (que ya incluyen descuentos)
			state.precioTotal = items.reduce((total, item) => total + item.precioTotal, 0);
			state.pedidoId = id;
			if (items.length > 0) {
				const primerProductoAgregadoAt = getPrimerProductoAgregadoAtDesdeExpiracion(carritoExpiraAt);
				state.primerProductoAgregadoAt =
					primerProductoAgregadoAt || state.primerProductoAgregadoAt || Date.now();
			} else {
				state.primerProductoAgregadoAt = null;
			}
		},

		setDireccion: (state, action) => {
			state.direccionId = action.payload;
		},

		setDeliveryMethod: (state, action) => {
			state.deliveryMethod = action.payload;
		},

		setSucursal: (state, action) => {
			state.sucursalId = action.payload;
		},

		setNotas: (state, action) => {
			state.notas = action.payload;
		},
		setNotasCocinera: (state, action) => {
			state.notasCocinera = action.payload;
		},
		setCupon: (state, action) => {
			state.cupon = action.payload;
		},
	},
});

export const {
	agregarItem,
	quitarItem,
	incrementarCantidad,
	decrementarCantidad,
	limpiarCarrito,
	setCarrito,
	setCostoEnvio,
	setZonaEnvio,
	setZonaEnvioNombre,
	setComisionMP,
	setDireccion,
	setDeliveryMethod,
	setSucursal,
	setNotas,
	setNotasCocinera,
	setCupon,
} = carritoSlice.actions;

export default carritoSlice.reducer;
